import React from "react";
import { Box, Typography } from "@mui/material";
import { GATEWAY_STATUS } from "../constants";

/**
 * StatusBadge - Displays a styled status badge for gateway/device status
 * 
 * @param {string} status - The status to display (e.g., "ACTIVE", "PAUSED", "STOPPED")
 * @param {object} sx - Optional Material-UI sx prop for custom styling
 * 
 * @example
 * <StatusBadge status="ACTIVE" />
 * <StatusBadge status="PAUSED" />
 */
const StatusBadge = ({ status, sx = {} }) => {
    const normalizedStatus = status?.toLowerCase();
    
    // Define status configurations — using rgba for dark mode compatibility
    const statusConfig = {
        [GATEWAY_STATUS.ACTIVE.toLowerCase()]: {
            displayText: "On",
            backgroundColor: "rgba(76, 175, 80, 0.15)",
            dotColor: "#4CAF50",
            textColor: "#4CAF50"
        },
        [GATEWAY_STATUS.PAUSED.toLowerCase()]: {
            displayText: "Paused",
            backgroundColor: "rgba(255, 152, 0, 0.15)",
            dotColor: "#FF9800",
            textColor: "#FF9800"
        },
        [GATEWAY_STATUS.STOPPED.toLowerCase()]: {
            displayText: "Off",
            backgroundColor: "rgba(244, 67, 54, 0.15)",
            dotColor: "#F44336",
            textColor: "#F44336"
        },
        [GATEWAY_STATUS.INACTIVE.toLowerCase()]: {
            displayText: "Off",
            backgroundColor: "rgba(244, 67, 54, 0.15)",
            dotColor: "#F44336",
            textColor: "#F44336"
        },
        [GATEWAY_STATUS.STARTING.toLowerCase()]: {
            displayText: "Starting",
            backgroundColor: "rgba(33, 150, 243, 0.15)",
            dotColor: "#2196F3",
            textColor: "#2196F3"
        },
        [GATEWAY_STATUS.PAUSING.toLowerCase()]: {
            displayText: "Pausing",
            backgroundColor: "rgba(255, 152, 0, 0.15)",
            dotColor: "#FF9800",
            textColor: "#FF9800"
        },
        [GATEWAY_STATUS.STOPPING.toLowerCase()]: {
            displayText: "Stopping",
            backgroundColor: "rgba(244, 67, 54, 0.15)",
            dotColor: "#F44336",
            textColor: "#F44336"
        }
    };

    // Get config for current status or use default
    const config = statusConfig[normalizedStatus] || {
        displayText: status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "Unknown",
        backgroundColor: "var(--color-hover-neutral)",
        dotColor: "#757575",
        textColor: "var(--text-secondary-color)"
    };

    return (
        <Box sx={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: 1,
            padding: "4px 12px",
            borderRadius: "12px",
            backgroundColor: config.backgroundColor,
            ...sx
        }}>
            <Box sx={{ 
                width: "8px", 
                height: "8px", 
                borderRadius: "50%",
                backgroundColor: config.dotColor
            }} />
            <Typography variant="body2" sx={{ 
                fontWeight: 600, 
                fontSize: "13px",
                color: config.textColor
            }}>
                {config.displayText}
            </Typography>
        </Box>
    );
};

export default StatusBadge;
